/* eslint-disable no-restricted-syntax */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Box, Dialog, DialogActions, DialogContent, Grid, InputAdornment, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ContainedButton } from 'components/ContainedButton';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropDown } from 'components/dropdown';
import { AssociationType, DocumentFile, SearchParams } from '../../types';
import {
  closestCenter, DndContext, DragEndEvent, DragOverEvent, DragOverlay, DragStartEvent, KeyboardSensor, PointerSensor, rectIntersection, useSensor, useSensors
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { orderDocumentsFilesAlphabetically } from '../../utils/orderDocumentsAlphabetically';
import { DocumentFileSortableItem } from './components/DocumentFileSortableItem';
import { DroppableList } from './components/DroppableList';
import debounce from 'debounce';
import { getTokens } from 'utils/search';
import { searchDocumentsFiles } from '../../utils/filterDocuments';

export const useStyles = makeStyles((theme: any) => ({
  searchIcon: { color: theme.palette.text.shadow, fontSize: '0.875rem' },
  searchBox: {
    '& .MuiFilledInput-root': {
      textTransform: 'none',
      fontSize: '0.875rem',
      color: theme.palette.common.black,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      backgroundColor: theme.palette.background.default,
      padding: '0.7rem'
    },
    '& .MuiInputBase-inputHiddenLabel': { padding: 0 }
  },
  container: { padding: theme.spacing(2) },
  title: {
    fontWeight: 500,
    padding: theme.spacing(2),
    color: theme.palette.primary.main,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: theme.accent.main,
    paddingBottom: theme.spacing(2)
  },
  content: {
    padding: theme.spacing(2)
  },
  subtitle: {
    fontWeight: 500,
    color: theme.palette.primary.main,
    paddingBottom: "0.3rem"
  },
  titleList: {
    fontWeight: 500,
    fontSize: "0.9rem",
    color: theme.palette.primary.main,
    padding: theme.spacing(0.5),
    textAlign: "center",
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: theme.accent.main,
  },
  dropzoneContainer: {
    borderWidth: 2,
    borderRadius: 2,
    borderColor: 'lightgray',
    borderStyle: 'dashed',
    padding: theme.spacing(5),
    width: '90%'
  },
  fileContainer: {
    borderRadius: '15px',
    background: '#ececec',
    width: 'auto',
    paddingRight: theme.spacing(2)
  },
  removeFileContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: '#646b6e',
  },
  actionButtons: { padding: theme.spacing(2) },
  errorIconContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
}));

type EditBundleContentModalProps = {
	kind: string,
	isOpen: boolean,
	onClose: () => void,
	isSavingLoading: boolean,
	parties: AssociationType[],
	estates: AssociationType[],
	includedDocuments: string[],
	onSave: (bundle: string[]) => void,
	allFilesinDocuments: DocumentFile[]
}

export const EditBundleContentModal: React.FC<EditBundleContentModalProps> = ({
  kind,
  isOpen,
  onClose,
  isSavingLoading,
  parties,
  estates,
  includedDocuments,
  onSave,
  allFilesinDocuments
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [allDocuments, setAllDocuments] = useState<DocumentFile[]>(allFilesinDocuments
    .filter(doc =>
      ["accounting", "auto", "template", "upload"].includes(doc.kind)
			&& !includedDocuments.some((source) => source === doc.source)
    ));
  const [bundle, setBundle] = useState<DocumentFile[]>(allFilesinDocuments
    .filter(doc => includedDocuments.some((source) => source === doc.source))
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const findItemContainer = (id: string) => {
    if (allDocuments.find(item => item._id === id)) return 'allDocuments';
    if (bundle.find(item => item._id === id)) return 'bundle';
    return null;
  };

  const addDocumentToBundle = (document: DocumentFile) => {
    setAllDocuments(items => items.filter(item => item._id !== document._id));
    setBundle(items => [...items, document]);
  };

  const removeDocumentFromBundle = (document: DocumentFile) => {
    setBundle(items => items.filter(item => item._id !== document._id));
    setAllDocuments(items => orderDocumentsFilesAlphabetically([...items, document]));
  };

  const [activeDocumentFileId, setActiveDocumentFileId] = useState<string | null>("");

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (!over) return;

    const activeContainer = findItemContainer(active.id as string);

    const overContainer = over.data.current?.sortable?.containerId ?? "";

    if (!activeContainer || !overContainer) return;

    const activeItem = activeContainer === "allDocuments"
      ? allDocuments.find(item => item._id === active.id)
      : bundle.find(item => item._id === active.id);

    if (!activeItem) return;

    if (activeContainer === 'allDocuments' && overContainer === 'bundle') {
      setAllDocuments(items => items.filter(item => item._id !== active.id));
      setBundle(items => [...items, activeItem]);
    } else if (activeContainer === 'bundle' && overContainer === 'allDocuments') {
      setBundle(items => items.filter(item => item._id !== active.id));
      setAllDocuments(items => orderDocumentsFilesAlphabetically([...items, activeItem]));
    }
  };

  const collisionDetectionStrategy = (args: any) => {
    const containerCollisions = rectIntersection({
      ...args,
      droppableContainers: args.droppableContainers.filter(
        (container: any) => container.id === "bundle" || container.id === "allDocuments"
      ),
    });

    if (containerCollisions.length > 0) {
      for (const collision of containerCollisions) {
        const containerId = collision.id as string;
        if ((containerId === "allDocuments" && allDocuments.length === 0) || (containerId === "bundle" && bundle.length === 0)) {
          return [collision];
        }
      }
    }

    return closestCenter(args);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveDocumentFileId(active.id as string);
  };

  const activeDocumentFile: DocumentFile | null = activeDocumentFileId
    ? allDocuments.concat(bundle).find(({ _id }) => _id === activeDocumentFileId)!
    : null;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDocumentFileId(null);

    if (!over) return;

    const activeContainer = findItemContainer(active.id as string);
    const overContainer = ["allDocuments", "bundle"].includes(over.id as string)
      ? over.id as string
      : findItemContainer(over.id as string);

    if (!activeContainer || !overContainer) return;

    if (activeContainer !== overContainer) {
      const activeItem = activeContainer === "bundle"
        ? bundle.find(item => item._id === active.id)
        : allDocuments.find(item => item._id === active.id);
      if (!activeItem) return;

      if (overContainer === "bundle") {
        setBundle(prevBundle => [...prevBundle, activeItem]);
        setAllDocuments(prevAllDoc => prevAllDoc.filter(({ _id }) => _id !== activeItem?._id));
      } else {
        setBundle(prevBundle => prevBundle.filter(({ _id }) => _id !== activeItem._id));
        setAllDocuments(prevAllDoc => orderDocumentsFilesAlphabetically([...prevAllDoc, activeItem]));
      }
    } else if (overContainer === "bundle") {
      const activeIndex = bundle.findIndex(({ _id }) => _id === active.id);
      const overIndex = bundle.findIndex(({ _id }) => _id === over.id);

      if (activeIndex !== overIndex) {
        setBundle((items) => arrayMove(items, activeIndex, overIndex));
      }
    }
  };

  const debounceSearch = useMemo(
    () => debounce(() => {
      const { query, party, property } = searchParamsRef.current;
      if (query.length <= 0 && party.length <= 0 && property.length <= 0) {
        setAllDocuments(allFilesinDocuments
          .filter(doc =>
            ["accounting", "auto", "template", "upload"].includes(doc.kind)
            && !includedDocuments.includes(doc._id) && !bundle.some(({ _id }) => _id === doc._id)
          )
        );
        return;
      }

      const { queryTokens, partyTokens, propertyTokens } = getTokens({
        query,
        party,
        property,
      });

      let documentsList: DocumentFile[] = [];
      let searchWords = '';
      documentsList = searchDocumentsFiles({
        documents: allFilesinDocuments
          .filter((doc) => ["accounting", "auto", "template", "upload"].includes(doc.kind)
            && !includedDocuments.includes(doc._id)),
        queryTokens,
        partyTokens: partyTokens ? [partyTokens.join(" ")] : undefined,
        propertyTokens,
      });

      const words = [
        ...(queryTokens || []),
        ...(partyTokens || []),
        ...(propertyTokens || []),
      ].join('|');

      searchWords = `(${words})`;
      setAllDocuments(documentsList.filter(doc =>
        ["accounting", "auto", "template", "upload"].includes(doc.kind)
        && !includedDocuments.includes(doc._id) && !bundle.some(({ _id }) => _id === doc._id)
      ));
    }, 1000),
    [bundle, allFilesinDocuments, includedDocuments]
  );
  const searchParamsRef = useRef<SearchParams>({
    query: '',
    party: '',
    property: '',
  });

  React.useEffect(() => () => debounceSearch.clear(), [debounceSearch]);

  const onSearch = ({
    value,
    type = 'query',
  }: {
		value: string;
		type: keyof SearchParams;
	}) => {
    debounceSearch.clear();
    searchParamsRef.current[type] = value;
    debounceSearch();
  };

  return (
    <Dialog open={isOpen} keepMounted={false} maxWidth="md" fullWidth>
      <DialogContent style={{ padding: 0, maxWidth: "100%", overflowX: "hidden" }}>
        <Box>
          <Typography variant="h5" className={classes.title}>
            {kind === "zip" ? t('documents:zip-content') : t('documents:pdf-content')}
          </Typography>

          <Box className={classes.content}>
            <Typography variant="body1" className={classes.subtitle}>
              {t('documents:filter-by')}
            </Typography>

            <Grid
              item
              container
              xs={12}
              alignItems={isMobile ? "stretch" : "center"}
              wrap="nowrap"
              style={{ gap: "0.7rem", paddingBottom: "1.5rem", flexDirection: isMobile ? "column" : "row" }}
              data-testid="document-filters"
            >
              <Grid item xs={isMobile ? 12 : 5}>
                <TextField
                  hiddenLabel
                  placeholder={t('documents:search-documents')}
                  InputProps={{
									  endAdornment: (
  <InputAdornment position="end">
    <FontAwesomeIcon icon={faSearch} className={classes.searchIcon} />
  </InputAdornment>
									  ),
                  }}
                  variant="filled"
                  className={classes.searchBox}
                  fullWidth
                  onChange={(e) => onSearch({ value: e.target.value, type: 'query' })}
                />
              </Grid>
              <Grid item xs={isMobile ? 12 : 4}>
                <DropDown
                  options={parties}
                  optionValue="name"
                  optionText="name"
                  onChange={(value) => onSearch({ value, type: 'party' })}
                  defaultText={t('documents:select-party')}
                />
              </Grid>
              <Grid item xs={isMobile ? 12 : 4}>
                <DropDown
                  options={estates}
                  optionValue="name"
                  optionText="name"
                  onChange={(value) => onSearch({ value, type: 'property' })}
                  defaultText={t('documents:select-property')}
                />
              </Grid>
            </Grid>

            <Box
              data-testid="document-lists"
              style={{
							  display: "flex",
							  flexDirection: isMobile ? "column" : "row",
							  gap: "1rem",
							  width: "100%",
							  minWidth: 0
              }}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={collisionDetectionStrategy}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <DroppableList
                  documents={allDocuments}
                  title={t('documents:available-files')}
                  droppableContainerId="allDocuments"
                  isMobile={isMobile}
                  actionLabel={t('common:add')}
                  actionIcon={faPlus}
                  onAction={addDocumentToBundle}
                />
                <DroppableList
                  documents={bundle}
                  title={t('documents:included')}
                  droppableContainerId="bundle"
                  isMobile={isMobile}
                  actionLabel={t('common:remove')}
                  actionIcon={faMinus}
                  actionVariant="outlined"
                  onAction={removeDocumentFromBundle}
                />
                <DragOverlay adjustScale={false} style={{ maxWidth: "500px", maxHeight: "100px" }}>
                  {activeDocumentFile ? (
                    <DocumentFileSortableItem documentFile={activeDocumentFile} />
                  ) : null}
                </DragOverlay>
              </DndContext>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className={classes.actionButtons}>
        <ContainedButton text={t('common:close')} onClick={onClose} disabled={isSavingLoading} />
        <ContainedButton
          text={t('common:save')}
          isLoading={isSavingLoading}
          onClick={() => onSave(bundle.map(({ source }) => source))}
          disabled={isSavingLoading}
        />
      </DialogActions>
    </Dialog>
  );
};
